import React, { useEffect, useState, useRef } from 'react';
import { FiLogOut, FiUser, FiBell, FiMenu, FiChevronDown } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api'; // Axios instance
import { socket } from '../../socket/socket';
import { useNavigate } from 'react-router-dom';
import { generateKOT } from '../../utils/kotPrinter';

const Navbar = ({ isMobileOpen, setIsMobileOpen }) => {
  const { user, logout } = useAuth();
  const [notificationCount, setNotificationCount] = useState(0);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch unread notifications count from API
  const fetchUnreadCount = async () => {
    if (!user) return;
    try {
      if (user.role === 'superadmin') {
        const res = await api.get('/superadmin/notifications/stats');
        if (res.data.success) {
          setNotificationCount(res.data.data?.unread || 0);
        }
      } else {
        const res = await api.get('/notifications/unread-count');
        if (res.data.success) {
          setNotificationCount(res.data.data.count);
        }
      }
    } catch (err) {
      console.error('Failed to fetch unread notifications:', err.message);
    }
  };

  useEffect(() => {
    if (!user) return;

    // Fetch initial unread count
    fetchUnreadCount();

    // Socket room join is handled globally in Sidebar.js
    // Only set up notification listeners here

    socket.on('notification:new', (payload) => {
      fetchUnreadCount()
    });

    socket.on('NEW_KOT', (payload) => {
       generateKOT(payload.orderId,user)
    });
    
    socket.on('notification:read', (payload) => {
      fetchUnreadCount()
    });

    socket.on("notification:planExpiryReminder", fetchUnreadCount)

    socket.on("deliveryRequest:approved", fetchUnreadCount)
    socket.on("deliveryRequest:rejected", fetchUnreadCount)


    // Super admin specific: plan request notifications
    if (user.role === 'superadmin') {
      socket.on('planRequest:new', (payload) => {
        fetchUnreadCount();
      });

      socket.on('vendor:newRegistration', () => {
      console.log("vendor:newRegistration-navbar")
        fetchUnreadCount();
      });

      socket.on('deliveryRequest:new', () => {
        fetchUnreadCount();
      });

      socket.on('vendor:documentsUpdated', () => {
        fetchUnreadCount();
      });

      socket.on('superadmin:notificationsCount', (payload) => {
        fetchUnreadCount()
      });
    }

    // No cleanup needed for permanent components, but NEW_KOT needs specific handling
    // to prevent duplicate KOT printing on component re-renders
    return () => {
      socket.off('NEW_KOT');
    };

  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Mobile Menu Button + Title */}
          <div className="flex items-center space-x-4 flex-1">
            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              onClick={() => setIsMobileOpen && setIsMobileOpen(!isMobileOpen)}
              title="Toggle Menu"
            >
              <FiMenu className="text-xl" />
            </button>
            
            <h1 className="text-xl font-bold text-gray-900 truncate">
              {user?.role === 'superadmin' && 'Super Admin Panel'}
              {user?.role === 'vendor' && user?.restaurantName}
              {user?.role === 'captain' && 'Captain Dashboard'}
              {user?.role === 'vendorOwner' && 'Owner Panel'}
            </h1>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center space-x-4">
            {/* Notification Bell */}
            {['vendor', 'captain', 'superadmin', 'vendorOwner'].includes(user?.role) && (
              <button 
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                onClick={() => {
                  if(user?.role === 'vendor'){
                    navigate('/vendor/notification');
                  } else if (user?.role === 'superadmin') {
                    navigate('/superadmin/notifications');
                  }
                }}
                title="Notifications"
              >
                <FiBell className="text-xl" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-medium">
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                )}
              </button>
            )}

            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                title="Profile Menu"
              >
                <FiUser className="text-gray-600" />
                <span className="text-sm font-medium text-gray-700 truncate max-w-[120px] hidden sm:inline">
                  {user?.role === 'vendor' ? user?.username : user?.name || user?.email}
                </span>
                <FiChevronDown className={`text-gray-600 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.role === 'vendor' ? user?.username : user?.name || user?.email}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.role === 'superadmin' && 'Super Admin'}
                      {user?.role === 'vendor' && 'Vendor'}
                      {user?.role === 'captain' && 'Captain'}
                      {user?.role === 'vendorOwner' && 'Owner'}
                    </p>
                  </div>
                  
                  <div className="py-1">
                    {/* <button 
                      onClick={() => {
                        setIsProfileDropdownOpen(false);
                        // Navigate to profile page if needed
                        // navigate('/profile');
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                    >
                      <FiUser className="mr-3 text-gray-400" />
                      Profile
                    </button> */}
                    
                    <button 
                      onClick={() => {
                        setIsProfileDropdownOpen(false);
                        logout();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                    >
                      <FiLogOut className="mr-3" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
